import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

const NewsletterSubscription = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const handleSubscribe = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!recaptchaToken) {
      alert('Please complete the reCAPTCHA verification');
      return;
    }

    if (!name.trim() || !email.trim()) {
      alert('Please fill in all fields');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          recaptchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubscribed(true);
        setName('');
        setEmail('');
        setRecaptchaToken(null);
        recaptchaRef.current?.reset();
      } else {
        if (response.status === 409) {
          alert('You are already subscribed to our newsletter.');
        } else if (response.status === 400) {
          alert(data.error || 'Please check your input and try again.');
        } else {
          alert(data.message || 'Subscription failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('Subscription failed:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubscribed) {
    return (
      <div className="flex py-[88px] px-6 flex-col items-center gap-4 self-stretch rounded-3xl border border-[#EDEDED] bg-[#FAFAFA] animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center">
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M26.6667 8L12 22.6667L5.33334 16"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="flex flex-col items-center gap-4 w-full">
          <h3 className="font-manrope font-bold text-black text-2xl text-center leading-8">
            Successfully Subscribed
          </h3>
          <p className="font-manrope text-[#999999] text-base text-center leading-normal">
            Check your email to confirm and get your first insights
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAFAFA] rounded-3xl border border-[#EDEDED] p-6 animate-fade-in flex flex-col items-center gap-4">
      <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center">
        <svg
          width="32"
          height="23"
          viewBox="0 0 32 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id="mail-gradient"
              x1="16"
              y1="0"
              x2="16"
              y2="22.8572"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#0F6EEA" />
              <stop offset="0.5" stopColor="#0989BE" />
              <stop offset="1" stopColor="#0098A2" />
            </linearGradient>
          </defs>
          <path
            d="M29.7143 0H2.28571C1.67951 0 1.09807 0.240816 0.669491 0.669491C0.240816 1.09807 0 1.67951 0 2.28571V20.5714C0 21.1776 0.240816 21.7591 0.669491 22.1877C1.09807 22.6163 1.67951 22.8571 2.28571 22.8571H29.7143C30.3205 22.8571 30.9019 22.6163 31.3305 22.1877C31.7592 21.7591 32 21.1776 32 20.5714V2.28571C32 1.67951 31.7592 1.09807 31.3305 0.669491C30.9019 0.240816 30.3205 0 29.7143 0ZM29.7143 6.85714L16 13.7143L2.28571 6.85714V2.28571L16 9.14286L29.7143 2.28571V6.85714Z"
            fill="url(#mail-gradient)"
          />
          <path
            d="M29.7143 0H2.28571C1.67951 0 1.09807 0.240816 0.669491 0.669491C0.240816 1.09807 0 1.67951 0 2.28571V20.5714C0 21.1776 0.240816 21.7591 0.669491 22.1877C1.09807 22.6163 1.67951 22.8571 2.28571 22.8571H29.7143C30.3205 22.8571 30.9019 22.6163 31.3305 22.1877C31.7592 21.7591 32 21.1776 32 20.5714V2.28571C32 1.67951 31.7592 1.09807 31.3305 0.669491C30.9019 0.240816 30.3205 0 29.7143 0ZM29.7143 6.85714L16 13.7143L2.28571 6.85714V2.28571L16 9.14286L29.7143 2.28571V6.85714Z"
            fill="white"
          />
        </svg>
      </div>

      <div className="flex flex-col items-center gap-4 w-full">
        <div className="flex flex-col items-start gap-4 w-full">
          <h3 className="font-manrope font-bold text-black text-2xl text-center leading-8 w-full">
            Subscribe for Insights
          </h3>
          <p className="font-manrope text-[#999999] text-base text-center leading-normal w-full">
            Get the latest articles on AI, data, and marketplace growth delivered to your inbox.
          </p>
        </div>

        <form onSubmit={handleSubscribe} className="flex flex-col items-center gap-4 w-full">
          <div className="flex flex-col items-center gap-4 w-full">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ose David"
              className="h-11 px-4 py-2.5 bg-white rounded-lg border border-[#E4E7EC] font-manrope text-[#333333] text-sm leading-7 outline-none focus:border-androia-blue transition-colors w-full"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ose.david@andoriaai.com"
              className="h-11 px-4 py-2.5 bg-white rounded-lg border border-[#E4E7EC] font-manrope text-[#333333] text-sm leading-7 outline-none focus:border-androia-blue transition-colors w-full"
            />
          </div>

          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
            onChange={handleRecaptchaChange}
            onExpired={() => setRecaptchaToken(null)}
          />

          <div className="flex flex-col items-center gap-3 w-full">
            <button
              type="submit"
              disabled={isLoading}
              className="h-12 px-4 flex items-center justify-center gap-2.5 w-full rounded-md text-white font-manrope font-bold text-base leading-5 transition-all hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(180deg, #0F6EEA 0%, #0989BE 50%, #0098A2 100%)',
              }}
            >
              {isLoading ? 'Subscribing...' : 'Subscribe'}
            </button>
            <p className="font-manrope text-[#808080] text-xs leading-7">
              No spam. Unsubscribe anytime.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsletterSubscription;
